import * as React from 'react';
import {mapValues, isEqual, get} from 'lodash';
import {Result, either} from '@packages/utilities/fp/result';
import {emptyObject} from '@packages/utilities/fp/utils';
import {type Point} from '@packages/utilities/PointUtils';

import {reportBug} from '@packages/utilities/reporter';
import {styled, emotionStyled} from '@packages/utilities/styled';
import {Input} from './Input';
import PseudoProvider from './PseudoProvider';
import draggable from './logic/draggable';
// eslint-disable-next-line webflow/no-dangerous-imports
import DragGhost from '@packages/systems/ui/shared-ui/DragGhost';
import WebflowRightInputTicks from './WebflowRightInputTicks';

import {
  reducer as numberInputReducer,
  actionTypes as numberInputActionTypes,
  actionCreators as numberInputActionCreators,
  WHEEL_TIMEOUT,
  type Action as NumberInputAction,
  type State as NumberInputState,
  type OnChangeMeta,
} from './WebflowNumberInput';
import {Select, Option} from './Select';

export type {OnChangeMeta};

import {clamp} from '@packages/systems/core/utils/NumberUtils';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

type RangeValue = {
  min: number;
  max: number;
  step: number;
  default?: number;
};

export type UnitConfig = {
  /** A unique identifier (also the default display name for the unit) */
  unit: string;
  /** Valid range of numerical values */
  range?: RangeValue;
  /** How the unit should appear in the dropdown menu */
  menuName?: string;
  /** How the unit should appear in the dropdown button when selected */
  buttonName?: string;
  dragSensitivity?: number;
};

type UnitValue = {
  value: any;
  unit: any;
};
type Props<T extends UnitValue> = {
  value?: T;
  // Safeguard to prevent production bugs where onChange(null) is called (currently onChange handlers expect a non-null value). This
  // property should be removed when all onChange handlers can handle null.
  required?: boolean;
  onChange: (value: T, arg2: OnChangeMeta) => void;
  // Escape hatch to force onChange to be called when one of these actionTypes are fired
  forceOnChangeForActions?: Set<(typeof actionTypes)[keyof typeof actionTypes]>;
  getUnit: (arg1: T) => string;
  parseValue: (currentUnit: string, inputValue: string) => Result<string, T>; // eslint-disable-line react/no-unused-prop-types
  getValue: (value: number | string, unit: string) => Partial<T>;
  getNumberFromValue: (arg1: T) => number | string | undefined;
  isValidInputValue: (currentUnit: string, inputValue: string) => boolean; // eslint-disable-line react/no-unused-prop-types
  onInvalidValueSubmitted?: (inputValue: string) => void;
  checkEquality?: (
    arg1?: T | null | undefined,
    arg2?: T | null | undefined
  ) => boolean;
  readonly units: Array<Readonly<UnitConfig>>;
  disabledUnits?: Array<string>;
  advancedUnits?: Set<string>;
  disabled?: boolean;
  autoFocus?: boolean;
  name?: string;
  style?: CSSProperties;
  selectMenuWidth?: number;
  placeholder: T;
  ['data-automation-id']?: string;
  ['aria-label']?: string;
  ['aria-describedby']?: string;
  dragSensitivity?: number;
  // Flag to indicate if the input should remain in focus while the user
  // is interacting with the dropdown unit menu
  shouldKeepInputFocused?: boolean;
  /** When true, allows drag/wheel on keyword values (prevents NaN). When false, blocks drag/wheel for keywords. */
  allowKeywordDrag?: boolean;
  _ref?: React.ForwardedRef<HTMLDivElement>;
};

type State<T> = {
  // stores props.value internally.
  _value: T | null | undefined;
  // Mutable internal value state which is derrived from props.value
  value: T | null | undefined;
  invalid: boolean;
  numberInputState: NumberInputState;
};

type Action =
  | NumberInputAction
  | {
      type: typeof actionTypes.SELECT_MENU_UNIT_CHANGED;
      payload: {
        unit: string;
      };
    };

export const CHANGE_START_TYPE = {changeType: 'changeStart'} as const;
export const CHANGE_TYPE = {changeType: 'change'} as const;
export const CHANGE_END_TYPE = {changeType: 'changeEnd'} as const;

export const actionTypes = {
  ...numberInputActionTypes,
  SELECT_MENU_UNIT_CHANGED: 'SELECT_MENU_UNIT_CHANGED' as const,
} as const;

export type WebflowUnitInputActionType = keyof typeof actionTypes;

const actionCreators = {
  ...numberInputActionCreators,

  // @ts-expect-error - TS7006 - Parameter 'unit' implicitly has an 'any' type.
  onSelectMenuUnitChange: (unit) => {
    return {type: actionTypes.SELECT_MENU_UNIT_CHANGED, payload: {unit}};
  },
} as const;

const fallbackRange = {
  min: Number.MIN_SAFE_INTEGER,
  max: Number.MAX_SAFE_INTEGER,
  step: 1,
} as const;

const fallbackUnitConfig = {range: fallbackRange} as const;
const __HACK__OPACITY_UNITLESS_CONFIG = {
  unit: '-',
  range: {step: 1, max: 100, min: 0},
} as const;
const getUnitConfig = <T extends UnitValue>(props: Props<T>, unit: string) => {
  const unitConfig = props.units.find((cfg) => cfg.unit === unit);

  // HACK: This avoids the reportBug call below for the `-` unit when unbinding
  // a number variable to the `opacity` property.
  if (props.name === 'effect-op-style' && unit === '-') {
    return __HACK__OPACITY_UNITLESS_CONFIG;
  }

  if (unitConfig == null) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug(
      'UnitInputInvalidUnit',
      `Invalid unit "${unit}" ${
        props.name ? ` for [name="${props.name}"]` : ''
      }. Valid units are: ${JSON.stringify(props.units.map((u) => u.unit))}`
    );
    return fallbackUnitConfig;
  }
  return unitConfig;
};

const isUnitDisabled = (
  disabledUnits: Props<any>['disabledUnits'],
  unit: string
): boolean => {
  return disabledUnits ? disabledUnits.includes(unit) : false;
};

const clampValue = <T extends UnitValue>(props: Props<T>, value: T): T => {
  const unit = props.getUnit(value);
  let numericValue = props.getNumberFromValue(value);
  // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'number'.
  if (isNaN(numericValue)) {
    numericValue = undefined;
  }
  const {range = fallbackRange} = getUnitConfig(props, unit);
  if (typeof numericValue === 'number' && range) {
    numericValue = clamp(range.min, range.max, numericValue);
  }
  // @ts-expect-error - TS2322 - Type 'Partial<T>' is not assignable to type 'T'. | TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
  return props.getValue(numericValue, unit);
};

const valuesAreEqual = <T extends UnitValue>(
  a: T | null | undefined,
  b: T | null | undefined,
  // @ts-expect-error - TS7006 - Parameter 'getValue' implicitly has an 'any' type.
  getValue,
  checkEquality: typeof isEqual
) =>
  (a == null && b == null) || (a != null && b != null && checkEquality(a, b));

/**
 * Synchronizes all state values. Called when upsteam values change.
 */

const syncValue = <T extends UnitValue>(
  props: Props<T>,
  state: State<T>,
  incomingValue?: T | null
): State<T> => {
  const value = incomingValue && clampValue(props, incomingValue);

  const numericValue = value != null ? props.getNumberFromValue(value) : null;
  const numberValue = numericValue == null ? undefined : Number(numericValue);

  return {
    ...state,
    invalid: false,
    value,
    numberInputState: {
      ...state.numberInputState,
      inputValue: numericValue == null ? '' : String(numericValue),
      value: numberValue,
      draggedValue:
        state.numberInputState.draggedValue != null && numericValue != null
          ? Number(numericValue)
          : null,
    },
  };
};

/**
 * commits values upstream. Called when downstream value changes (number inpput)
 */

const commitValue = <T extends UnitValue>(
  props: Props<T>,
  state: State<T>,
  incomingValue: T
): State<T> => {
  const value = clampValue(props, incomingValue);

  return {
    ...state,
    invalid: false,
    value,
  };
};

export const reducer = <T extends UnitValue>(
  state: State<T>,
  action: Action,
  props: Props<T>
): State<T> => {
  if (action.type === actionTypes.SELECT_MENU_UNIT_CHANGED) {
    const {
      payload: {unit},
    } = action;
    if (props.value && unit === props.getUnit(props.value)) {
      return state;
    }
    let numericValue = props.value && props.getNumberFromValue(props.value);

    if (
      numericValue == null ||
      (typeof numericValue === 'string' && Number.isNaN(Number(numericValue)))
    ) {
      numericValue = get(getUnitConfig(props, unit), ['range', 'default']);
    }
    // @ts-expect-error - TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
    const value = props.getValue(numericValue, unit);

    // @ts-expect-error - TS2322 - Type 'State<UnitValue>' is not assignable to type 'State<T>'. | TS2345 - Argument of type 'Props<T>' is not assignable to parameter of type 'Props<UnitValue>'.
    return syncValue(props, state, value);
  }

  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  const defaultUnit = props.units[0].unit;
  const unit = props.value ? props.getUnit(props.value) : defaultUnit;
  const unitConfig = getUnitConfig(props, unit);

  const castKeywordAsNumber = (value: string) =>
    Number.isNaN(Number(value)) ? '0' : value && String(value);

  const {range: defaultRange} = getUnitConfig(props, defaultUnit);

  // Check if value is a keyword OR if unit itself is 'auto' (treated as keyword unit)
  const isKeyword = Number.isNaN(Number(props.value?.value)) || unit === 'auto';

  // Block drag/wheel for keyword values unless allowKeywordDrag is true
  if (
    isKeyword &&
    !props.allowKeywordDrag &&
    (action.type === actionTypes.INPUT_DRAG_STARTED ||
      action.type === actionTypes.INPUT_WHEEL_STARTED)
  ) {
    return state;
  }

  const newNumberInputState = numberInputReducer(
    state.numberInputState,
    action,
    // Flow HACK: Obj-spreading here avoids an invalid type error:
    {
      ...(unitConfig.range || defaultRange || fallbackRange),
      castKeywordAsNumber,
      validateOnKeyPress: false,
    },
    props.value && props.value.value
  );

  if (
    action.type === actionTypes.INPUT_ENTER_KEY_PRESSED ||
    action.type === actionTypes.INPUT_DRAG_ENDED ||
    action.type === actionTypes.INPUT_WHEEL_ENDED ||
    action.type === actionTypes.INPUT_WHEEL_STARTED ||
    action.type === actionTypes.INPUT_DRAG_STARTED ||
    action.type === actionTypes.INPUT_BLURRED ||
    action.type === actionTypes.INPUT_RIGHT_UP_TICK_CLICKED ||
    action.type === actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED ||
    action.type === actionTypes.INPUT_ARROW_UP_KEY_PRESSED ||
    action.type === actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED
  ) {
    let updatedState: State<T> = {
      ...state,
      numberInputState: newNumberInputState,
    };

    return either<string, State<T>>((_err) => {
      const {inputValue} = newNumberInputState;

      // Set value to null to trigger reset if empty + enter (https://github.com/webflow/webflow/issues/25374).
      if (
        action.type === actionTypes.INPUT_ENTER_KEY_PRESSED &&
        inputValue === '' &&
        props.required !== true
      ) {
        // if cleared, then set value to null so that it can be
        // reset
        updatedState = syncValue<T>(props, updatedState, null);
        return {
          ...updatedState,
          invalid: false,
        };
      } else {
        // We couldn't parse a number + input pair, but we only want to
        // invalidate this if it's not considered a valid input string (which
        // e.g. may not contain the unit, but could e.g. also be something like
        // "auto" or "calc(...)", etc)
        const invalid = !props.isValidInputValue(unit, inputValue);
        if (invalid) {
          if (
            action.type === actionTypes.INPUT_ENTER_KEY_PRESSED ||
            action.type === actionTypes.INPUT_BLURRED
          ) {
            props.onInvalidValueSubmitted?.(inputValue);
          }

          if (action.type === actionTypes.INPUT_BLURRED) {
            updatedState = syncValue<T>(props, updatedState, props.value);
            return {
              ...updatedState,
              invalid: false,
            };
          } else {
            return {
              ...updatedState,
              invalid: true,
            };
          }
        } else {
          return syncValue<T>(
            props,
            updatedState,
            // @ts-expect-error - TS2322 - Type 'Partial<T>' is not assignable to type 'T'. | TS2345 - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number'.
            props.getValue(
              // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | number'.
              newNumberInputState.value,
              props.value ? props.getUnit(props.value) : defaultUnit
            )
          );
        }
      }
    })((parsedValue: T) => {
      const stateWithValue = syncValue(
        props,
        updatedState,
        clampValue(props, parsedValue)
      );

      const invalid = isUnitDisabled(
        props.disabledUnits,
        props.getUnit(parsedValue)
      );

      if (invalid) {
        return {
          ...updatedState,
          value: props.value,
          invalid,
        };
      } else {
        return stateWithValue;
      }
    })(props.parseValue(unit, newNumberInputState.inputValue));
  }

  state = {
    ...state,
    numberInputState: newNumberInputState,
    value: props.value,
  };

  // just want to sync values here if the number input state has changed
  // so that onChange gets called in this component.
  if (
    action.type === actionTypes.INPUT_DRAGGED ||
    action.type === actionTypes.INPUT_WHEELED
  ) {
    // @ts-expect-error - TS2322 - Type 'State<UnitValue>' is not assignable to type 'State<T>'.
    state = commitValue(
      // @ts-expect-error - TS2345 - Argument of type 'Props<T>' is not assignable to parameter of type 'Props<UnitValue>'.
      props,
      state,
      props.getValue(
        // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | number'.
        newNumberInputState.value,
        isKeyword ? defaultUnit : unit
      )
    );
  }

  return state;
};

const Grid = emotionStyled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  transform: 'translate3d(0,0,0)',
  position: 'relative',
});

const StyledTextInput = draggable(
  ({style, paddingRight, onFocus, ...props}) => (
    <Input
      autoComplete="off"
      gridColumn="1 / -1"
      gridRow="1 / 2"
      paddingRight={`${paddingRight}px`}
      onFocus={(e) => {
        e.target.select();
        onFocus && onFocus();
      }}
      {...props}
      style={style}
    />
  )
);

const StyledSelect = styled(Select)(
  ({theme: {typography, gutters, colors, UnitInput}, marginRight}) => ({
    ...typography.labelTiny,
    color: colors.foregroundDim,
    placeSelf: 'stretch',
    gridRow: '1 / 2',
    gridColumn: '2 / 3',
    zIndex: 1,
    minWidth: gutters.base * 4,
    borderRadius: 1,
    height: `calc(100% - ${gutters.x4 / 2}px)`,
    textAlign: 'center',
    background: UnitInput.background,
    boxShadow: UnitInput.boxShadow,
    borderWidth: 0,
    marginLeft: 1,
    marginTop: 1,
    marginBottom: 1,
    marginRight: marginRight || 1,
    position: 'relative',
    right: 2,
  }),
  {
    hover: ({theme: {colors, TextInput}, disabled}) => {
      if (disabled) {
        return {
          color: TextInput.disabled.color,
        };
      }

      return {
        color: colors.foregroundVivid,
      };
    },

    disabled: ({theme: {TextInput, colors}}) => ({
      color: TextInput.disabled.color,
      background: TextInput.disabled.background,
      borderColor: colors.border,
      boxShadow: 'none',
      opacity: 1,
    }),
  }
);

// HACK: offset set here otherwise the menu doesn't line up with input.
const menuOffset = {top: 4, left: 3} as const;

// @ts-expect-error - TS7006 - Parameter 'nextState' implicitly has an 'any' type. | TS7006 - Parameter 'prevState' implicitly has an 'any' type. | TS7006 - Parameter 'action' implicitly has an 'any' type.
const getChangeMeta = (nextState, prevState, action): OnChangeMeta | null => {
  switch (action.type) {
    case actionTypes.INPUT_WHEEL_STARTED:
    case actionTypes.INPUT_DRAG_STARTED:
      return {type: action.type, changeType: CHANGE_START_TYPE.changeType};

    case actionTypes.INPUT_BLURRED:
    case actionTypes.INPUT_VALUE_CHANGED:
    case actionTypes.INPUT_ENTER_KEY_PRESSED:
    case actionTypes.INPUT_ARROW_UP_KEY_PRESSED:
    case actionTypes.INPUT_RIGHT_UP_TICK_CLICKED:
    case actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED:
    case actionTypes.INPUT_DRAGGED:
    case actionTypes.INPUT_WHEELED:
    case actionTypes.INPUT_ARROW_DOWN_KEY_PRESSED: {
      return {type: action.type, changeType: CHANGE_TYPE.changeType};
    }

    case actionTypes.INPUT_KEY_RELEASED:
    case actionTypes.INPUT_WHEEL_ENDED:
    case actionTypes.INPUT_DRAG_ENDED: {
      return {type: action.type, changeType: CHANGE_END_TYPE.changeType};
    }

    case actionTypes.SELECT_MENU_UNIT_CHANGED: {
      if (
        nextState.value &&
        prevState.value &&
        nextState.value.unit === prevState.value.unit
      ) {
        return null;
      }
      return {type: action.type, changeType: CHANGE_END_TYPE.changeType};
    }

    // By default we don't fire a change event:
    default: {
      return null;
    }
  }
};

const makeDispatchHandlers = (dispatch: (action: Action) => void) =>
  // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
  mapValues(actionCreators, (actionCreator) => (...args) => {
    // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
    const action = actionCreator(...args);
    if (!action || !action.type) {
      return;
    }
    // @ts-expect-error - TS2345 - Argument of type 'Record<string, any>' is not assignable to parameter of type 'Action'.
    dispatch(action);
  });

// TODO: Base these on `theme`
const getPaddingRight = (unitButtonName: string) => {
  if (unitButtonName && unitButtonName.length >= 11) {
    return 72;
  }
  if (unitButtonName && unitButtonName.length >= 4) {
    return 32;
  }
  if (unitButtonName && unitButtonName.length >= 3) {
    return 24;
  }
  if (unitButtonName && unitButtonName.length >= 2) {
    return 20;
  }

  return 20;
};

const inputResizeCursor = {cursor: 'ew-resize'} as const;

const defaultState = {
  _value: undefined,
  value: undefined,
  invalid: false,
  numberInputState: {
    invalid: false,
    changing: false,
    focus: false,
    alt: false,
    shift: false,
    inputValue: '',
    value: undefined,
    draggedValue: null,
    precision: null,
  },
} as const;
const disallowTextDragging = (e: any) => e.preventDefault();

export default class UnitInput<T extends UnitValue> extends React.PureComponent<
  Props<T>,
  State<T>
> {
  declare wheelTimeout: number;
  // Initialized in constructor to work around SWC minification bug
  // See: https://github.com/swc-project/swc/issues/11368
  handlers: ReturnType<typeof makeDispatchHandlers>;

  state: State<T> = defaultState;

  constructor(props: Props<T>) {
    super(props);
    this.handlers = makeDispatchHandlers(this.dispatch);
  }

  static getDerivedStateFromProps<T extends UnitValue>(
    props: Props<T>,
    state: State<T>
  ) {
    const {checkEquality = isEqual, getValue} = props;

    if (props.placeholder && props.placeholder.value == null) {
      return null;
    }

    // check if props.value has changed (won't match _value). This is necessary against _value since setState
    // triggers getDerivedStateFromProps, and we don't want state to be re-computed _while_ it's changing.
    if (!valuesAreEqual(state._value, props.value, getValue, checkEquality)) {
      let newState: State<T> = {...state, _value: props.value};

      // internal _value !== props.value, so we need to compare value === props.value. If there's a mismatch, then
      // props.value was likely changed _outside_ of this component (happens when changing auto to px)
      if (
        !valuesAreEqual(props.value, newState.value, getValue, checkEquality)
      ) {
        newState = syncValue(props, newState, props.value);
      }
      return newState;
    }

    return null;
  }

  dispatch = (action: Action) => {
    const prevState = this.state;
    const newState = reducer(prevState, action, this.props);
    const newValue = newState.value;
    const {checkEquality = isEqual, placeholder} = this.props;

    const meta = getChangeMeta(newState, prevState, action);

    const hasValueChanged =
      !newState.invalid && !checkEquality(this.props.value, newValue as T);
    const isEphemeralHook =
      meta != null &&
      (meta.changeType === CHANGE_START_TYPE.changeType ||
        meta.changeType === CHANGE_END_TYPE.changeType);
    const isCommittingEqualToPlaceholder =
      (action.type === actionTypes.INPUT_ENTER_KEY_PRESSED ||
        action.type === actionTypes.INPUT_DRAG_ENDED ||
        action.type === actionTypes.INPUT_WHEEL_ENDED ||
        action.type === actionTypes.INPUT_RIGHT_UP_TICK_CLICKED ||
        action.type === actionTypes.INPUT_RIGHT_DOWN_TICK_CLICKED ||
        action.type === actionTypes.INPUT_BLURRED) &&
      Boolean(placeholder) &&
      prevState.numberInputState.inputValue ===
        String(this.props.getNumberFromValue(placeholder));

    const shouldDispatch: boolean =
      hasValueChanged || isEphemeralHook || isCommittingEqualToPlaceholder;

    const forceOnChangeForActions =
      this.props.forceOnChangeForActions ?? new Set();
    const forceDispatch = forceOnChangeForActions.has(action.type);

    this.setState(
      newState,
      shouldDispatch || forceDispatch
        ? () => {
            if (hasValueChanged || forceDispatch) {
              // @ts-expect-error - TS2345 - Argument of type 'T | null | undefined' is not assignable to parameter of type 'T'.
              this.props.onChange(newValue, meta);
              return;
            }

            if (
              isEphemeralHook &&
              (action.type === actionTypes.INPUT_DRAG_ENDED ||
                action.type === actionTypes.INPUT_WHEEL_ENDED)
            ) {
              // @ts-expect-error - TS2345 - Argument of type 'T | null | undefined' is not assignable to parameter of type 'T'.
              this.props.onChange(newValue, meta);
              return;
            }

            if (
              action.type === actionTypes.INPUT_DRAG_STARTED ||
              action.type === actionTypes.INPUT_WHEEL_STARTED
            ) {
              // @ts-expect-error - TS2345 - Argument of type 'T | null | undefined' is not assignable to parameter of type 'T'.
              this.props.onChange(newValue, meta);
              return;
            }
          }
        : undefined
    );
  };

  handleKeyDown = (e: KeyboardEvent) => {
    this.handlers.onAltKeyDown(e);
    this.handlers.onShiftKeyDown(e);
  };

  handleKeyUp = (e: KeyboardEvent) => {
    this.handlers.onAltKeyUp(e);
    this.handlers.onShiftKeyUp(e);
  };

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown, false);
    document.removeEventListener('keyup', this.handleKeyUp, false);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown, false);
    document.addEventListener('keyup', this.handleKeyUp, false);
  }
  onFocus = () => {
    const {getNumberFromValue, placeholder, value} = this.props;
    const {numberInputState} = this.state;

    if (
      !value &&
      placeholder &&
      (numberInputState.inputValue ===
        String(getNumberFromValue(placeholder)) ||
        getNumberFromValue(placeholder) == null)
    ) {
      this.setState(
        {
          numberInputState: {
            ...numberInputState,
            inputValue: '',
          },
        },
        () => this.handlers.onFocus()
      );
    } else {
      this.handlers.onFocus();
    }
  };

  getPlaceholder = () => {
    const {placeholder: placeholderSource, getNumberFromValue} = this.props;
    if (placeholderSource) {
      const placeholder = getNumberFromValue(placeholderSource);
      if (placeholder != null) {
        return String(placeholder);
      }
    }
  };

  handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (event.altKey) {
      event.stopPropagation();
      event.preventDefault();
      clearTimeout(this.wheelTimeout);
      // @ts-expect-error - TS2322 - Type 'Timeout' is not assignable to type 'number'.
      this.wheelTimeout = setTimeout(this.handlers.onWheelEnded, WHEEL_TIMEOUT);
      const deltaY = event.deltaY;
      if (this.state.numberInputState.changing === false) {
        const startValue =
          this.state.numberInputState.value ?? this.props.placeholder?.value;
        const initialValue = typeof startValue === 'number' ? startValue : null;
        this.handlers.onWheelStarted({initialValue});
      } else {
        this.handlers.onWheeled({x: deltaY});
      }
    }
  };

  onDragStart = (point: Point) => {
    if (this.state.numberInputState.alt === false) return;

    const startValue =
      this.state.numberInputState.value ?? this.props.placeholder?.value;
    const initialValue = typeof startValue === 'number' ? startValue : null;

    this.handlers.onDragStarted({...point, initialValue});
  };

  onDragEnd = () => {
    if (this.state.numberInputState.changing) this.handlers.onDragEnded();
  };

  handleUnitDropdownMouseDownEvent = (e: React.MouseEvent<HTMLElement>) => {
    // Stop mouseDown interactions in the dropdown from stealing the focus of the input
    // field, forcing the input field to remain in its focused state.
    if (this.props.shouldKeepInputFocused) {
      e.preventDefault();
    }
  };

  render() {
    let {units} = this.props;
    const {
      name,
      style: {paddingRight: rightGutter, ...style} = emptyObject,
      selectMenuWidth = 60,
      disabled,
      autoFocus = false,
      placeholder,
      getUnit,
      value,
      getNumberFromValue,
      advancedUnits,
      'data-automation-id': dataAutomationId,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      allowKeywordDrag = false,
    } = this.props;
    const {onSelectMenuUnitChange, onBlur, onChange, onKeyDown, onKeyUp} =
      this.handlers;
    const isSingleUnit = units.length === 1;

    const {
      invalid,
      numberInputState: {focus, alt},
      numberInputState,
    } = this.state;

    const currentValue = value || placeholder;

    const unit = getUnit(currentValue);
    const isUnknownUnit = units.every((v) => v.unit !== unit);
    if (isUnknownUnit) {
      try {
        units = units.concat({
          unit,
          menuName: unit.toUpperCase(),
        });
      } catch (e: any) {
        reportBug(
          'UnitInputInvalidUnit',
          'Error concatenating unit in WebflowUnitInput',
          {
            team: 'area-design-system',
            ticket: 'https://webflow.atlassian.net/browse/SHSVS-8091',
            currentValue,
            value,
            placeholder,
            unit,
            units,
            error: e,
          }
        );
      }
    }

    // Check if value is a keyword OR if unit itself is 'auto' (treated as keyword unit)
    const isKeyword =
      Number.isNaN(Number(currentValue?.value)) || unit === 'auto';

    // Disable drag/arrows for keywords unless allowKeywordDrag is true
    const shouldDisableForKeyword = isKeyword && !allowKeywordDrag;

    const resizeStyle =
      alt && !disabled && !shouldDisableForKeyword
        ? inputResizeCursor
        : undefined;

    return (
      <>
        <DragGhost
          dragging={!disabled && this.state.numberInputState.changing}
          cursor={shouldDisableForKeyword ? undefined : 'ew-resize'}
        />
        <PseudoProvider>
          {({hover}) => (
            <Grid
              style={style}
              data-legacy-ds="WebflowUnitInput"
              ref={this.props._ref}
            >
              <StyledTextInput
                // Allows undoing/redoing (in ShortcutHelper.js) if the Input is focused
                data-wf-text-input
                paddingRight={
                  rightGutter ||
                  getPaddingRight(
                    // @ts-expect-error - TS2339 - Property 'buttonName' does not exist on type 'Readonly<UnitConfig> | { readonly range: { readonly min: number; readonly max: number; readonly step: 1; }; }'.
                    getUnitConfig(this.props, unit).buttonName || unit
                  )
                }
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={autoFocus}
                name={name}
                style={resizeStyle}
                value={numberInputState.inputValue}
                placeholder={
                  placeholder && !focus
                    ? String(getNumberFromValue(placeholder))
                    : ''
                }
                disabled={disabled}
                invalid={!this.state.numberInputState.changing && invalid}
                focus={focus}
                onFocus={this.onFocus}
                onWheel={focus ? this.handleWheel : undefined}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onChange={onChange}
                onDrop={disallowTextDragging}
                data-automation-id={dataAutomationId}
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                mode="delta"
                dragging={this.state.numberInputState.changing}
                onDragStart={this.onDragStart}
                onDrag={this.handlers.onDragged}
                onDragEnd={this.onDragEnd}
              />

              {this.state.numberInputState.alt &&
              hover &&
              !disabled &&
              !shouldDisableForKeyword ? (
                <WebflowRightInputTicks
                  onTopClick={this.handlers.onRightUpTickClick}
                  onBottomClick={this.handlers.onRightDownTickClick}
                />
              ) : (
                <PseudoProvider>
                  {({hover: _hover}) => (
                    <StyledSelect
                      data-prevent-global-event-handlers
                      id={name ? `${name}-UnitSelect` : null}
                      menuOffset={menuOffset}
                      disabled={disabled || isSingleUnit}
                      animated={false}
                      indicator={false}
                      menuWidth={selectMenuWidth}
                      transparentBackground={isSingleUnit}
                      blend={!isSingleUnit}
                      unitBorder
                      handleChange={onSelectMenuUnitChange}
                      tabIndex={-1}
                      value={unit}
                      hover={_hover}
                      marginRight={rightGutter}
                      onMouseDown={this.handleUnitDropdownMouseDownEvent}
                      onItemMouseDown={this.handleUnitDropdownMouseDownEvent}
                    >
                      {units.reduce<Array<any>>(
                        (memo, {unit: type, menuName, buttonName}) => {
                          // This hides every advanced unit from the menu *unless* it is the current unit
                          if (
                            advancedUnits &&
                            advancedUnits.has(type) &&
                            unit !== type
                          ) {
                            return memo;
                          }
                          memo.push(
                            <Option
                              aria-label={buttonName}
                              key={type}
                              // @ts-expect-error - TS2322 - Type '{ "aria-label": string | undefined; key: string; hover: boolean | undefined; disabled: boolean; value: string; label: string; buttonLabel: string; }' is not assignable to type 'IntrinsicAttributes'.
                              hover={_hover}
                              disabled={isUnitDisabled(
                                this.props.disabledUnits,
                                type
                              )}
                              value={type}
                              label={menuName != null ? menuName : type}
                              buttonLabel={
                                buttonName != null ? buttonName : type
                              }
                            />
                          );
                          return memo;
                        },
                        []
                      )}
                    </StyledSelect>
                  )}
                </PseudoProvider>
              )}
            </Grid>
          )}
        </PseudoProvider>
      </>
    );
  }
}
